import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getOptionGroup,
    getOptionGroupOrderLocks,
    searchOptionGroups,
    OptionGroupSearchParams,
    changeDirectOptionGroupStatus,
    changeRoomOptionGroupStatus,
    updateDirectOptionGroup,
    updateRoomOptionGroup,
    updateDirectOptionGroupLinks,
    updateRoomOptionGroupLinks,
    reorderOptionGroupsByRoom,
    DirectLinkStatusUpdateParams,
    RoomLinkStatusUpdateParams,
    DirectOptionGroupUpdateParams,
    RoomOptionGroupUpdateParams,
    LinkToResidentialUnitsOptions,
    LinkToRoomsOptions,
    deleteOptionGroup,
} from './option-groups';
import { Optional } from '@ygtechnologies/bygr-frontend-component-library';
import { optionGroupsKeys } from './option-groups.queryKeys';
import { loadProjectOverview } from '@/state/seller/project-overview/creator';
import { useAppDispatch, useAppSelector } from '@/utils/hooks';

function useRefreshProjectOverview() {
    const dispatch = useAppDispatch();
    const projectId = useAppSelector((state) => state.global.activeProject?.id);
    return () => {
        if (projectId) {
            loadProjectOverview(dispatch, projectId);
        }
    };
}

export function useSearchOptionGroups(options: Optional<OptionGroupSearchParams, 'projectId'>) {
    return useQuery({
        queryKey: options ? optionGroupsKeys.search(options as OptionGroupSearchParams) : optionGroupsKeys.root,
        queryFn: () => searchOptionGroups(options as OptionGroupSearchParams),
        // enabled: !!options && !!options.projectId,
    });
}

export function useOptionGroup(optionGroupId: number | undefined) {
    return useQuery({
        queryKey: ['getOptionGroup', optionGroupId],
        queryFn: () => getOptionGroup(optionGroupId!),
        enabled: !!optionGroupId,
    });
}

export function useOptionGroupOrderLocks(optionGroupId: number | undefined) {
    return useQuery({
        queryKey: ['optionGroupOrderLocks', optionGroupId],
        queryFn: () => getOptionGroupOrderLocks(optionGroupId!),
        enabled: !!optionGroupId,
    });
}

export function useChangeDirectOptionGroupStatus() {
    const queryClient = useQueryClient();
    const refreshOverview = useRefreshProjectOverview();
    return useMutation({
        mutationFn: ({ optionGroupId, options }: { optionGroupId: number; options: DirectLinkStatusUpdateParams }) =>
            changeDirectOptionGroupStatus(optionGroupId, options),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
            refreshOverview();
        },
    });
}

export function useChangeRoomOptionGroupStatus() {
    const queryClient = useQueryClient();
    const refreshOverview = useRefreshProjectOverview();
    return useMutation({
        mutationFn: ({ optionGroupId, options }: { optionGroupId: number; options: RoomLinkStatusUpdateParams }) =>
            changeRoomOptionGroupStatus(optionGroupId, options),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
            refreshOverview();
        },
    });
}

export function useUpdateDirectOptionGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ optionGroupId, options }: { optionGroupId: number; options: DirectOptionGroupUpdateParams }) =>
            updateDirectOptionGroup(optionGroupId, options),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
        },
    });
}

export function useUpdateRoomOptionGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ optionGroupId, options }: { optionGroupId: number; options: RoomOptionGroupUpdateParams }) =>
            updateRoomOptionGroup(optionGroupId, options),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
        },
    });
}

export function useUpdateDirectOptionGroupLinks() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ optionGroupId, options }: { optionGroupId: number; options: LinkToResidentialUnitsOptions }) =>
            updateDirectOptionGroupLinks(optionGroupId, options),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
        },
    });
}

export function useUpdateRoomOptionGroupLinks() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ optionGroupId, options }: { optionGroupId: number; options: LinkToRoomsOptions }) =>
            updateRoomOptionGroupLinks(optionGroupId, options),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
        },
    });
}

export function useDeleteOptionGroup() {
    const queryClient = useQueryClient();
    const refreshOverview = useRefreshProjectOverview();
    return useMutation({
        mutationFn: (optionGroupId: number) => deleteOptionGroup(optionGroupId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
            refreshOverview();
        },
    });
}

export function useReorderOptionGroupsByRoom() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ roomId, sortedOptionGroupIds }: { roomId: number; sortedOptionGroupIds: number[] }) =>
            reorderOptionGroupsByRoom(roomId, sortedOptionGroupIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
        },
    });
}
